import '../chunk-TAYHZHK7.js';
import { useOptionalApiClient } from '../chunk-HTQXG6NE.js';
import '../chunk-WJJBT3ZR.js';
import '../chunk-I37XHMWY.js';
import '../chunk-3WCKEFHJ.js';
import '../chunk-2UGAFJLN.js';
import '../chunk-FAVK2ZL5.js';
import '../chunk-ZDGWOR5S.js';
import '../chunk-Z2NUSCHM.js';
import '../chunk-C2JZ6HDR.js';
import '../chunk-EOFYHHEG.js';
import '../chunk-ANGPR3MS.js';
import '../chunk-G3N5GV7A.js';
import '../chunk-GSFYO3YU.js';
import '../chunk-V4MOACUU.js';
import '../chunk-SWFXEBPS.js';
import '../chunk-5NACIYNI.js';
import '../chunk-7BRTZT5T.js';
import '../chunk-NCCYLVD3.js';
import '../chunk-IW2JHXYK.js';
import '../chunk-PEIGN6MO.js';
import '../chunk-AFBF4GCN.js';
import '../chunk-36R7DNJW.js';
import '../chunk-TNAHBB5Q.js';
import '../chunk-QOM4QXAM.js';
import '../chunk-676CI6I4.js';
import '../chunk-NHXI3UAE.js';
import { Badge } from '../chunk-2WDEGOCV.js';
import '../chunk-MBTLYAMU.js';
import { Button } from '../chunk-PLP233UC.js';
import '../chunk-FFX3LZW7.js';
import * as React from 'react';
import { qualityStateOf, QUALITY_STATE_LABEL, qualityStateDetail, callerQualityLoader, summaryFromRow, qualityRollup, QUALITY_STATES, qualitySource, qualityTerminal } from '@authe/fe-commons/quality';
import { shortenHash } from '@authe/fe-commons/chains';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';

var box = "rounded-lg border bg-card p-4 space-y-3";
var field = "w-full rounded-md border bg-background px-3 py-2 text-sm";
var human = (v) => (v ?? "unknown").replaceAll("_", " ");
var errorText = (e) => e instanceof Error ? e.message : String(e);
var STATE_VARIANT = { complete: "success", gaps: "warning", not_assessable: "secondary", not_assessed: "outline" };
function QualityBadge({ summary, className }) {
  const state = qualityStateOf(summary);
  return /* @__PURE__ */ jsx(Badge, { variant: STATE_VARIANT[state], className: className ?? "text-3xs", title: qualityStateDetail(summary), "data-quality-state": state, children: QUALITY_STATE_LABEL[state] });
}
var subscribeNothing = () => () => {
};
var noRevision = () => 0;
function useCallerQuality(rows, scope) {
  const client = useOptionalApiClient();
  const loader = React.useMemo(() => client ? callerQualityLoader(client) : void 0, [client]);
  const revision = React.useSyncExternalStore(loader?.subscribe ?? subscribeNothing, loader?.getRevision ?? noRevision, noRevision);
  const own = React.useMemo(() => rows.map(summaryFromRow), [rows]);
  const [checked, setChecked] = React.useState();
  React.useEffect(() => {
    if (!loader || own.every(Boolean)) return;
    let live = true;
    void Promise.all(rows.map((row, i) => own[i] ? void 0 : loader.load(row, scope))).then((values) => {
      if (live) setChecked({ rows, revision, values });
    });
    return () => {
      live = false;
    };
  }, [loader, rows, own, scope, revision]);
  return React.useMemo(
    () => own.map((summary, i) => summary ?? (checked?.rows === rows && checked.revision === revision ? checked.values[i] : void 0)),
    [own, checked, rows, revision]
  );
}
function RowQualityBadge({ row, scope, className }) {
  const rows = React.useMemo(() => [row], [row]);
  return /* @__PURE__ */ jsx(QualityBadge, { summary: useCallerQuality(rows, scope)[0], className });
}
function QualityRollup({ summaries, noun = "transactions", className }) {
  const rollup = React.useMemo(() => qualityRollup(summaries), [summaries]);
  if (!rollup.total || !rollup.weakest) return null;
  const parts = QUALITY_STATES.filter((state) => state !== "complete" && rollup.counts[state] > 0).map((state) => `${rollup.counts[state]} ${state === "gaps" ? "with gaps" : QUALITY_STATE_LABEL[state].toLowerCase()}`);
  return /* @__PURE__ */ jsxs("div", { role: "status", "aria-label": "Decoding quality of this list", className: className ?? "flex flex-wrap items-center gap-2 text-xs text-muted-foreground", children: [
    /* @__PURE__ */ jsx("span", { className: "font-medium text-foreground", children: "Decoding quality" }),
    /* @__PURE__ */ jsx(Badge, { variant: STATE_VARIANT[rollup.weakest], className: "text-3xs", title: "The weakest member's state: a group is as reliable as its weakest member", children: QUALITY_STATE_LABEL[rollup.weakest] }),
    /* @__PURE__ */ jsxs("span", { children: [
      rollup.counts.complete,
      " of ",
      rollup.total,
      " ",
      noun,
      " complete",
      parts.length ? ` \xB7 ${parts.join(" \xB7 ")}` : ""
    ] })
  ] });
}
function QualitySummaryView({ summary }) {
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap gap-x-6 gap-y-2 text-sm", children: [
    /* @__PURE__ */ jsxs("span", { children: [
      "Policy: ",
      /* @__PURE__ */ jsx("strong", { children: human(summary?.policy_result) })
    ] }),
    /* @__PURE__ */ jsxs("span", { children: [
      "Completeness: ",
      /* @__PURE__ */ jsx("strong", { children: human(summary?.completeness) })
    ] }),
    summary?.review && /* @__PURE__ */ jsxs("span", { children: [
      "Review: ",
      /* @__PURE__ */ jsx("strong", { children: human(summary.review.status) })
    ] }),
    /* @__PURE__ */ jsxs("span", { children: [
      "Evaluator: ",
      summary?.evaluator_version ?? "unavailable"
    ] })
  ] });
}
function QualityAssessmentView({ assessment }) {
  const snapshot = assessment.provenance?.execution_snapshot;
  const hash = typeof snapshot?.effective_config_hash === "string" ? snapshot.effective_config_hash : void 0;
  const replay = snapshot?.replay;
  return /* @__PURE__ */ jsxs("div", { className: "space-y-3", children: [
    /* @__PURE__ */ jsx(QualitySummaryView, { summary: assessment }),
    /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap gap-x-6 gap-y-2 text-sm", children: [
      /* @__PURE__ */ jsxs("span", { children: [
        "Pipeline: ",
        /* @__PURE__ */ jsx("strong", { children: String(snapshot?.pipeline_id ?? "unavailable") }),
        snapshot?.pipeline_revision != null && ` \xB7 revision ${String(snapshot.pipeline_revision)}`
      ] }),
      /* @__PURE__ */ jsxs("span", { children: [
        "Configuration: ",
        /* @__PURE__ */ jsx("code", { title: hash, children: hash ? hash.slice(0, 16) : "unavailable" })
      ] })
    ] }),
    /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground", children: "This measures decoding evidence and policy compliance. It does not prove the transaction's meaning is correct." }),
    /* @__PURE__ */ jsxs("p", { className: "text-sm", children: [
      "Configuration source: ",
      /* @__PURE__ */ jsx("strong", { children: human(String(snapshot?.configuration_source ?? "unavailable")) }),
      ". Recorded replay: ",
      /* @__PURE__ */ jsx("strong", { children: replay?.status === "available" ? "available" : "unavailable" }),
      replay?.reason && ` (${human(replay.reason)})`,
      ". External metadata is not pinned."
    ] }),
    /* @__PURE__ */ jsxs("details", { children: [
      /* @__PURE__ */ jsx("summary", { className: "cursor-pointer", children: "Configuration, versions and provenance" }),
      /* @__PURE__ */ jsx("pre", { className: "max-h-80 overflow-auto whitespace-pre-wrap break-all text-xs", children: JSON.stringify(assessment.provenance ?? { status: "unavailable" }, null, 2) })
    ] }),
    /* @__PURE__ */ jsxs("details", { children: [
      /* @__PURE__ */ jsx("summary", { className: "cursor-pointer", children: "Findings and policy" }),
      /* @__PURE__ */ jsx("pre", { className: "max-h-96 overflow-auto whitespace-pre-wrap break-all text-xs", children: JSON.stringify({ policy: assessment.policy, artifacts: assessment.artifacts, checks: assessment.checks }, null, 2) })
    ] })
  ] });
}
function QualityConsole({ client, transactionIds, operator = false }) {
  const source = React.useMemo(() => qualitySource(client), [client]);
  const [mode, setMode] = React.useState("assess_stored");
  const [configurationSource, setConfigurationSource] = React.useState("current");
  const recorded = mode === "redecode_compare" && configurationSource === "recorded";
  const [ids, setIds] = React.useState((transactionIds ?? []).join("\n"));
  const [chain, setChain] = React.useState("");
  const [protocol, setProtocol] = React.useState("");
  const [limit, setLimit] = React.useState(transactionIds?.length || 100);
  const [pipeline, setPipeline] = React.useState("");
  const [revision, setRevision] = React.useState("");
  const [policy, setPolicy] = React.useState("");
  const [subject, setSubject] = React.useState("");
  const [baseline, setBaseline] = React.useState("");
  const [from, setFrom] = React.useState("");
  const [until, setUntil] = React.useState("");
  const [viewName, setViewName] = React.useState("");
  const [viewId, setViewId] = React.useState();
  const [views, setViews] = React.useState([]);
  const [run, setRun] = React.useState();
  const [runs, setRuns] = React.useState([]);
  const [members, setMembers] = React.useState([]);
  const [next, setNext] = React.useState();
  const [assessment, setAssessment] = React.useState();
  const [error, setError] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [historyLoaded, setHistoryLoaded] = React.useState(false);
  const activeRunId = React.useRef(void 0);
  const attempt = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const request = () => ({
    mode,
    selection: {
      transaction_ids: ids.split(/[\s,]+/).filter(Boolean),
      max_transactions: limit,
      ...chain ? { chain_id: chain.trim() } : {},
      ...protocol ? { protocol_id: protocol.trim() } : {},
      ...viewId ? { saved_view_id: viewId } : {},
      ...from ? { from_timestamp: Math.floor((/* @__PURE__ */ new Date(from + "Z")).getTime() / 1e3) } : {},
      ...until ? { to_timestamp: Math.floor((/* @__PURE__ */ new Date(until + "Z")).getTime() / 1e3) } : {}
    },
    configuration_source: recorded ? "recorded" : "current",
    ...!recorded && pipeline ? { pipeline_id: pipeline.trim() } : {},
    ...!recorded && revision ? { pipeline_version: Number(revision) } : {},
    ...policy ? { policy: JSON.parse(policy) } : {},
    ...operator && subject ? { subject: JSON.parse(subject) } : {},
    ...baseline ? { baseline_run_id: baseline } : {}
  });
  const refresh = React.useCallback(async (id, select = true) => {
    if (select) {
      activeRunId.current = id;
      setAssessment(void 0);
    }
    if (activeRunId.current !== id) return;
    const [job, page] = await Promise.all([source.get(id), source.results(id)]);
    if (activeRunId.current !== id) return;
    setRun(job);
    setRuns((old) => old.map((item) => item.id === job.id ? job : item));
    setMembers(page.items);
    setNext(page.next_cursor);
    if (qualityTerminal(job.status)) callerQualityLoader(client).clear();
  }, [source, client]);
  React.useEffect(() => {
    if (!run || qualityTerminal(run.status)) return;
    const timer = setInterval(() => {
      void refresh(run.id, false).catch((e) => {
        if (activeRunId.current === run.id) setError(errorText(e));
      });
    }, 2500);
    return () => clearInterval(timer);
  }, [run?.id, run?.status, refresh]);
  const loadRequest = (r) => {
    setMode(r.mode);
    setConfigurationSource(r.configuration_source ?? "current");
    setIds((r.selection.transaction_ids ?? []).join("\n"));
    setChain(r.selection.chain_id ?? "");
    setProtocol(r.selection.protocol_id ?? "");
    setLimit(r.selection.max_transactions);
    setPipeline(r.pipeline_id ?? "");
    setRevision(r.pipeline_version?.toString() ?? "");
    setPolicy(r.policy ? JSON.stringify(r.policy, null, 2) : "");
    setSubject(r.subject ? JSON.stringify(r.subject, null, 2) : "");
    setBaseline(r.baseline_run_id ?? "");
    setFrom(r.selection.from_timestamp ? new Date(r.selection.from_timestamp * 1e3).toISOString().slice(0, 16) : "");
    setUntil(r.selection.to_timestamp ? new Date(r.selection.to_timestamp * 1e3).toISOString().slice(0, 16) : "");
  };
  return /* @__PURE__ */ jsxs("section", { "aria-label": "Decoding quality runs", className: box, children: [
    /* @__PURE__ */ jsx("h2", { className: "text-lg font-semibold", children: "Decoding quality" }),
    /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground", children: "Assess saved evidence, compare a pipeline profile, or publish a new decode. Every run freezes its selected inputs and keeps per-transaction results." }),
    /* @__PURE__ */ jsxs("form", { className: "space-y-3", onSubmit: (e) => {
      e.preventDefault();
      void attempt(async () => {
        const job = await source.submit(request());
        setAssessment(void 0);
        await refresh(job.id);
      });
    }, children: [
      /* @__PURE__ */ jsxs("label", { className: "block text-sm", children: [
        "Action",
        /* @__PURE__ */ jsxs("select", { className: field, value: mode, onChange: (e) => setMode(e.target.value), children: [
          /* @__PURE__ */ jsx("option", { value: "assess_stored", children: "Assess stored transactions" }),
          /* @__PURE__ */ jsx("option", { value: "redecode_compare", children: "Re-decode and compare" }),
          /* @__PURE__ */ jsx("option", { value: "redecode_publish", children: "Re-decode and publish" })
        ] })
      ] }),
      /* @__PURE__ */ jsxs("label", { className: "block text-sm", children: [
        "Transaction IDs",
        /* @__PURE__ */ jsx("textarea", { className: field, rows: 3, value: ids, onChange: (e) => setIds(e.target.value), placeholder: "Canonical IDs, separated by commas or new lines" })
      ] }),
      operator && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsxs("div", { className: "grid gap-3 md:grid-cols-3", children: [
          /* @__PURE__ */ jsxs("label", { className: "text-sm", children: [
            "Chain ID",
            /* @__PURE__ */ jsx("input", { className: field, value: chain, onChange: (e) => setChain(e.target.value), placeholder: "All chains" })
          ] }),
          /* @__PURE__ */ jsxs("label", { className: "text-sm", children: [
            "Protocol ID",
            /* @__PURE__ */ jsx("input", { className: field, value: protocol, onChange: (e) => setProtocol(e.target.value), placeholder: "All protocols" })
          ] }),
          /* @__PURE__ */ jsxs("label", { className: "text-sm", children: [
            "Maximum transactions",
            /* @__PURE__ */ jsx("input", { className: field, type: "number", min: 1, max: 1e4, value: limit, onChange: (e) => setLimit(Number(e.target.value)) })
          ] })
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "grid gap-3 md:grid-cols-2", children: [
          /* @__PURE__ */ jsxs("label", { className: "text-sm", children: [
            "From (UTC)",
            /* @__PURE__ */ jsx("input", { className: field, type: "datetime-local", value: from, onChange: (e) => setFrom(e.target.value) })
          ] }),
          /* @__PURE__ */ jsxs("label", { className: "text-sm", children: [
            "Until (UTC)",
            /* @__PURE__ */ jsx("input", { className: field, type: "datetime-local", value: until, onChange: (e) => setUntil(e.target.value) })
          ] })
        ] })
      ] }),
      mode === "assess_stored" && /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground", children: "Uses the saved decode and its recorded settings; current overrides are not applied." }),
      mode === "redecode_compare" && /* @__PURE__ */ jsxs("label", { className: "block text-sm", children: [
        "Configuration source",
        /* @__PURE__ */ jsxs("select", { className: field, value: configurationSource, onChange: (e) => setConfigurationSource(e.target.value), children: [
          /* @__PURE__ */ jsx("option", { value: "current", children: "Current effective settings" }),
          /* @__PURE__ */ jsx("option", { value: "recorded", children: "Recorded pipeline and overrides" })
        ] }),
        /* @__PURE__ */ jsx("span", { className: "text-xs", children: "Recorded replay keeps the original pipeline, protocol overrides and resolved confidence policy while using this application's decoder. Missing historical configuration fails explicitly; external metadata remains live." })
      ] }),
      mode !== "assess_stored" && !recorded && /* @__PURE__ */ jsxs("div", { className: "grid gap-3 md:grid-cols-2", children: [
        /* @__PURE__ */ jsxs("label", { className: "text-sm", children: [
          "Pipeline profile ID",
          /* @__PURE__ */ jsx("input", { className: field, value: pipeline, onChange: (e) => setPipeline(e.target.value), placeholder: "Default pipeline when empty" })
        ] }),
        /* @__PURE__ */ jsxs("label", { className: "text-sm", children: [
          "Pipeline revision",
          /* @__PURE__ */ jsx("input", { className: field, type: "number", min: 1, value: revision, onChange: (e) => setRevision(e.target.value), placeholder: "Latest, pinned at submission" })
        ] })
      ] }),
      /* @__PURE__ */ jsxs("details", { children: [
        /* @__PURE__ */ jsx("summary", { className: "cursor-pointer text-sm", children: "Policy and comparison options" }),
        /* @__PURE__ */ jsxs("div", { className: "space-y-3 pt-3", children: [
          /* @__PURE__ */ jsxs("label", { className: "block text-sm", children: [
            "Quality policy JSON",
            /* @__PURE__ */ jsx("textarea", { className: field, value: policy, rows: 4, onChange: (e) => setPolicy(e.target.value), placeholder: "Empty uses the recorded policy" })
          ] }),
          /* @__PURE__ */ jsxs("label", { className: "block text-sm", children: [
            "Baseline run ID",
            /* @__PURE__ */ jsx("input", { className: field, value: baseline, onChange: (e) => setBaseline(e.target.value), placeholder: "Reuse the frozen cohort and recorded assessment" })
          ] }),
          operator && /* @__PURE__ */ jsxs("label", { className: "block text-sm", children: [
            "Execution subject JSON",
            /* @__PURE__ */ jsx("textarea", { className: field, value: subject, rows: 3, onChange: (e) => setSubject(e.target.value), placeholder: '{"customer_id":"\u2026","org_id":"\u2026","tenant_id":"\u2026","user_id":null}' }),
            /* @__PURE__ */ jsx("span", { className: "text-xs", children: "Empty uses your current scope. Other scopes require internal pipeline administration permission." })
          ] })
        ] })
      ] }),
      mode === "redecode_publish" && /* @__PURE__ */ jsx("p", { role: "status", className: "text-sm font-medium", children: "Publishing replaces the selected transactions' stored decoded data." }),
      /* @__PURE__ */ jsxs("div", { className: "flex gap-2", children: [
        /* @__PURE__ */ jsx(Button, { type: "submit", disabled: busy || !operator && !ids.trim(), children: "Start quality run" }),
        /* @__PURE__ */ jsx(Button, { type: "button", variant: "outline", disabled: busy, onClick: () => void attempt(async () => {
          setRuns((await source.list()).items);
          setHistoryLoaded(true);
        }), children: "Run history" })
      ] })
    ] }),
    operator && /* @__PURE__ */ jsxs("details", { children: [
      /* @__PURE__ */ jsx("summary", { className: "cursor-pointer text-sm", children: "Saved cohorts" }),
      /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap gap-2 pt-3", children: [
        /* @__PURE__ */ jsx("input", { "aria-label": "Cohort name", className: field, value: viewName, onChange: (e) => setViewName(e.target.value), placeholder: "Cohort name" }),
        /* @__PURE__ */ jsx(Button, { variant: "outline", disabled: busy || !viewName.trim(), onClick: () => void attempt(async () => {
          await source.save(viewName, request(), run?.id);
          setMessage("Cohort saved");
        }), children: "Save cohort" }),
        /* @__PURE__ */ jsx(Button, { variant: "outline", disabled: busy, onClick: () => void attempt(async () => setViews((await source.views()).items ?? [])), children: "Load cohorts" }),
        views.map((v) => /* @__PURE__ */ jsx(Button, { variant: "ghost", onClick: () => {
          if (v.view_state?.quality) {
            loadRequest(v.view_state.quality.request);
            setViewId(v.id);
          }
        }, children: v.name }, v.id))
      ] })
    ] }),
    message && /* @__PURE__ */ jsx("p", { role: "status", children: message }),
    error && /* @__PURE__ */ jsx("p", { role: "alert", className: "text-sm text-destructive", children: error }),
    historyLoaded && runs.length === 0 && /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground", children: "No quality runs in this scope yet." }),
    runs.length > 0 && /* @__PURE__ */ jsx("div", { className: "max-h-56 overflow-auto", children: runs.map((r) => /* @__PURE__ */ jsxs("button", { className: "block py-2 text-left text-sm underline", onClick: () => void attempt(() => refresh(r.id)), children: [
      r.id,
      " \xB7 ",
      human(r.status),
      " \xB7 ",
      r.processed_count,
      " transactions"
    ] }, r.id)) }),
    run && /* @__PURE__ */ jsxs("div", { className: "space-y-3 border-t pt-3", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-3", children: [
        /* @__PURE__ */ jsx("strong", { children: human(run.status) }),
        /* @__PURE__ */ jsx("code", { className: "text-xs", children: run.id }),
        /* @__PURE__ */ jsxs("span", { className: "text-sm", children: [
          run.processed_count,
          "/",
          run.selected_count,
          " processed \xB7 ",
          run.failed_count,
          " failed"
        ] }),
        /* @__PURE__ */ jsx(Button, { variant: "outline", disabled: busy, onClick: () => void attempt(() => refresh(run.id)), children: "Refresh" }),
        /* @__PURE__ */ jsx(Button, { variant: "outline", disabled: busy || run.status === "succeeded", onClick: () => void attempt(async () => {
          await source.action(run.id, qualityTerminal(run.status) ? "retry" : "cancel");
          await refresh(run.id);
        }), children: qualityTerminal(run.status) ? "Retry failed / unfinished" : "Cancel run" })
      ] }),
      run.error && /* @__PURE__ */ jsx("p", { role: "alert", className: "text-sm text-destructive", children: run.error }),
      run.status === "selecting" && /* @__PURE__ */ jsxs("p", { className: "text-sm text-muted-foreground", children: [
        "Examined ",
        run.examined_count ?? 0,
        " transactions; freezing matches before evaluation."
      ] }),
      run.selection_shortfall > 0 && /* @__PURE__ */ jsxs("p", { className: "text-sm", children: [
        "The selection returned ",
        run.selection_shortfall,
        " fewer transactions than requested."
      ] }),
      /* @__PURE__ */ jsx("div", { className: "flex flex-wrap gap-4 text-sm", children: Object.entries(run.summary ?? {}).map(([k, v]) => /* @__PURE__ */ jsxs("span", { children: [
        human(k),
        ": ",
        /* @__PURE__ */ jsx("strong", { children: v })
      ] }, k)) }),
      /* @__PURE__ */ jsx("div", { className: "overflow-x-auto", children: /* @__PURE__ */ jsxs("table", { className: "w-full text-left text-sm", children: [
        /* @__PURE__ */ jsx("thead", { children: /* @__PURE__ */ jsxs("tr", { children: [
          /* @__PURE__ */ jsx("th", { className: "p-2", children: "Transaction" }),
          /* @__PURE__ */ jsx("th", { children: "Result" }),
          /* @__PURE__ */ jsx("th", { children: "Completeness" }),
          /* @__PURE__ */ jsx("th", { children: "Configuration" }),
          /* @__PURE__ */ jsx("th", { children: "Time" })
        ] }) }),
        /* @__PURE__ */ jsx("tbody", { children: members.map((m) => /* @__PURE__ */ jsxs("tr", { className: "border-t", children: [
          /* @__PURE__ */ jsxs("td", { className: "max-w-72 break-all p-2", children: [
            /* @__PURE__ */ jsx("button", { "aria-label": m.canonical_tx_id, title: m.canonical_tx_id, className: "underline font-mono", disabled: m.status !== "succeeded", onClick: () => void attempt(async () => setAssessment((await source.result(run.id, m.index)).assessment)), children: shortenHash(m.canonical_tx_id, { head: 16, tail: 8 }) }),
            m.error && /* @__PURE__ */ jsx("p", { role: "alert", children: m.error }),
            m.comparison != null && /* @__PURE__ */ jsxs("details", { children: [
              /* @__PURE__ */ jsx("summary", { children: "Comparison" }),
              /* @__PURE__ */ jsx("pre", { className: "max-h-64 overflow-auto text-xs", children: JSON.stringify(m.comparison, null, 2) })
            ] })
          ] }),
          /* @__PURE__ */ jsx("td", { children: human(m.summary?.policy_result ?? m.status) }),
          /* @__PURE__ */ jsx("td", { children: human(m.summary?.completeness) }),
          /* @__PURE__ */ jsx("td", { children: /* @__PURE__ */ jsx("code", { title: m.summary?.effective_config_hash, children: m.summary?.effective_config_hash?.slice(0, 12) ?? "unavailable" }) }),
          /* @__PURE__ */ jsx("td", { children: m.duration_ms == null ? "\u2014" : `${m.duration_ms} ms` })
        ] }, m.index)) })
      ] }) }),
      next && /* @__PURE__ */ jsx(Button, { variant: "outline", disabled: busy, onClick: () => void attempt(async () => {
        const page = await source.results(run.id, next);
        setMembers((old) => [...old, ...page.items]);
        setNext(page.next_cursor);
      }), children: "More results" }),
      assessment && /* @__PURE__ */ jsx(QualityAssessmentView, { assessment })
    ] })
  ] });
}
function TransactionQuality({ client, transactionId, initialAssessment, summary, scope, actions = false, pending = false }) {
  const key = `${transactionId}|${scope ?? ""}`;
  const [reply, setReply] = React.useState();
  const [loadingKey, setLoadingKey] = React.useState();
  const current = reply?.key === key ? reply : void 0;
  const assessment = current?.assessment ?? initialAssessment;
  const shown = summary ?? current?.summary;
  const loading = loadingKey === key;
  const load = React.useCallback(() => {
    setLoadingKey(key);
    return qualitySource(client).transaction(transactionId, scope).then((r) => setReply({ key, assessment: r.assessment, summary: r.summary, history: r.history, status: r.status })).catch((e) => setReply({ key, error: errorText(e) })).finally(() => setLoadingKey((active) => active === key ? void 0 : active));
  }, [client, transactionId, scope, key]);
  const unanswered = !assessment && !shown && !pending && !current && !loading;
  React.useEffect(() => {
    if (unanswered) void load();
  }, [unanswered, load]);
  const notAssessed = current?.status === "not_assessed" && !assessment;
  return /* @__PURE__ */ jsxs("section", { className: box, "aria-label": "Saved decoding quality", children: [
    /* @__PURE__ */ jsx("h3", { className: "font-semibold", children: "Decoding quality" }),
    assessment ? /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(QualityAssessmentView, { assessment }),
      current?.status === "assessed_on_read" && /* @__PURE__ */ jsx("p", { role: "status", className: "text-sm text-muted-foreground", children: "Assessed now from the stored decode. This transaction was stored without an assessment, so nothing was saved with it." })
    ] }) : shown ? /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(QualitySummaryView, { summary: shown }),
      /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground", children: "Saved summary. Load the assessment for its supporting evidence." })
    ] }) : notAssessed ? /* @__PURE__ */ jsxs("p", { role: "status", className: "text-sm", children: [
      "Not assessed. This transaction was stored without a quality assessment: it was decoded before assessments were recorded, or by a pipeline that does not record them.",
      actions ? " Run a quality check to assess its stored decode." : ""
    ] }) : current?.error ? null : /* @__PURE__ */ jsx("p", { role: "status", className: "text-sm text-muted-foreground", children: "Checking for a saved assessment\u2026" }),
    (assessment || shown || current) && /* @__PURE__ */ jsx(Button, { variant: "outline", disabled: loading, onClick: () => void load(), children: "Load assessment history" }),
    notAssessed && shown && /* @__PURE__ */ jsx("p", { role: "status", className: "text-sm", children: "No stored assessment. Start a quality run to assess this transaction." }),
    current?.error && /* @__PURE__ */ jsx("p", { role: "alert", children: current.error }),
    current?.history && /* @__PURE__ */ jsxs("details", { children: [
      /* @__PURE__ */ jsxs("summary", { children: [
        "Assessment history (",
        current.history.length,
        ")"
      ] }),
      /* @__PURE__ */ jsx("pre", { className: "max-h-80 overflow-auto whitespace-pre-wrap text-xs", children: JSON.stringify(current.history, null, 2) })
    ] }),
    actions && /* @__PURE__ */ jsxs("details", { open: notAssessed && !shown || void 0, children: [
      /* @__PURE__ */ jsx("summary", { className: "cursor-pointer", children: "Run quality check" }),
      /* @__PURE__ */ jsx(QualityConsole, { client, transactionIds: [transactionId] })
    ] })
  ] });
}

export { QualityAssessmentView, QualityBadge, QualityConsole, QualityRollup, QualitySummaryView, RowQualityBadge, TransactionQuality, useCallerQuality };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map